import { useMemo } from 'react';
import { isoWeekRange } from '../../lib/periodFormat';

/**
 * usePeriodoGroups — calcola tutti i raggruppamenti memoizzati richiesti
 * dalle sezioni MIT/Azioni della pagina periodo:
 *
 *   - azioniBySettimana : Map<ISOWeek, Azione[]>
 *   - mitByData         : Map<ISODate, MIT[]>
 *   - mitByArea         : Map<area,    MIT[]>   (per vista "giorno")
 *   - azioniByArea      : Map<area,    Azione[]>(per vista "giorno"/"settimana")
 *   - sevenDayBuckets   : 7 ISO dates Lun→Dom   (solo per vista "settimana")
 *
 * Tutte le funzioni di sorting/area-ordering sono qui, isolando la logica
 * di derivazione dalla render della UI.
 */
const AREA_ORDER = ['business', 'lavoro', 'personale', 'salute'];

function sortByAreaOrder([a], [b]) {
  const ia = AREA_ORDER.indexOf(a);
  const ib = AREA_ORDER.indexOf(b);
  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
}

export function usePeriodoGroups({ data, azioni, mit, gran, label }) {
  const azioniBySettimana = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    for (const a of azioni) {
      const k = a.settimana || 'senza-settimana';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [data, azioni]);

  const mitByData = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    for (const x of mit) {
      const k = x.data || 'senza-data';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(x);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [data, mit]);

  const mitByArea = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    for (const x of mit) {
      const kr = x.key_result_id ? data.key_results.find((k) => k.id === x.key_result_id) : null;
      const k = ((kr?.area || '') + '').toLowerCase() || 'senza-area';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(x);
    }
    return Array.from(m.entries()).sort(sortByAreaOrder);
  }, [data, mit]);

  const azioniByArea = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    for (const a of azioni) {
      const kr = a.key_result_id ? data.key_results.find((k) => k.id === a.key_result_id) : null;
      const k = ((a.area || kr?.area || '') + '').toLowerCase() || 'senza-area';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    }
    return Array.from(m.entries()).sort(sortByAreaOrder);
  }, [data, azioni]);

  const sevenDayBuckets = useMemo(() => {
    if (gran !== 'settimana' || !label) return null;
    const range = isoWeekRange(label);
    if (!range) return null;
    const [mon] = range;
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(mon);
      d.setUTCDate(mon.getUTCDate() + i);
      days.push(d.toISOString().slice(0, 10));
    }
    return days;
  }, [gran, label]);

  return { azioniBySettimana, mitByData, mitByArea, azioniByArea, sevenDayBuckets };
}
